import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../../context/GameContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiRefreshCw } = FiIcons;

function ColorGame() {
  const navigate = useNavigate();
  const { state, dispatch } = useGame();
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [questionsCompleted, setQuestionsCompleted] = useState(0);
  const [gameCompleted, setGameCompleted] = useState(false);

  const colors = [
    { name: 'red', color: '#ef4444', emoji: '🍎' },
    { name: 'blue', color: '#3b82f6', emoji: '🫐' },
    { name: 'green', color: '#22c55e', emoji: '🍀' },
    { name: 'yellow', color: '#eab308', emoji: '🌻' },
    { name: 'purple', color: '#a855f7', emoji: '🍇' },
    { name: 'orange', color: '#f97316', emoji: '🍊' },
    { name: 'pink', color: '#ec4899', emoji: '🌸' },
    { name: 'brown', color: '#a3a3a3', emoji: '🐻' }
  ];

  const generateQuestion = () => {
    const targetColor = colors[Math.floor(Math.random() * colors.length)];
    
    // Generate wrong answers
    const wrongAnswers = [];
    while (wrongAnswers.length < 3) {
      const wrong = colors[Math.floor(Math.random() * colors.length)];
      if (wrong.name !== targetColor.name && !wrongAnswers.some(w => w.name === wrong.name)) {
        wrongAnswers.push(wrong);
      }
    }
    
    const allAnswers = [targetColor, ...wrongAnswers].sort(() => Math.random() - 0.5);
    
    return {
      targetColor,
      answers: allAnswers,
      correctAnswer: targetColor.name
    };
  };

  useEffect(() => {
    setCurrentQuestion(generateQuestion());
  }, []);

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
    setShowResult(true);
    
    const isCorrect = answer === currentQuestion.correctAnswer;
    
    if (isCorrect) {
      dispatch({ type: 'ADD_SCORE', payload: 10 });
    } else {
      dispatch({ type: 'ADD_WRONG_ANSWER' });
    }
    
    setTimeout(() => {
      const newCompleted = questionsCompleted + 1;
      setQuestionsCompleted(newCompleted);
      
      if (newCompleted >= 5) {
        setGameCompleted(true);
        dispatch({ type: 'GAME_COMPLETED' });
      } else {
        setCurrentQuestion(generateQuestion());
        setSelectedAnswer(null);
        setShowResult(false);
      }
    }, 2000);
  };

  const restartGame = () => {
    setCurrentQuestion(generateQuestion());
    setSelectedAnswer(null);
    setShowResult(false);
    setQuestionsCompleted(0);
    setGameCompleted(false);
  };

  if (!currentQuestion && !gameCompleted) {
    return <div>Loading...</div>;
  }

  if (gameCompleted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex items-center justify-center p-4"
      >
        <div className="bg-white rounded-3xl p-8 shadow-2xl border-4 border-safari-200 text-center max-w-md">
          <div className="text-6xl mb-4">🌈</div>
          <h2 className="text-3xl font-kid text-jungle-700 mb-4">
            Colorful Success!
          </h2>
          <p className="font-comic text-jungle-600 mb-6">
            You completed the Color Explorer! You know your colors very well!
          </p>
          
          <div className="space-y-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={restartGame}
              className="w-full bg-gradient-to-r from-pink-500 to-purple-500 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Play Again
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gradient-to-r from-safari-500 to-safari-600 text-white p-3 rounded-2xl font-kid text-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Back to Games
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen p-4"
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center justify-between mb-8"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiArrowLeft} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Back</span>
          </button>

          <h1 className="text-3xl font-kid text-jungle-700">Color Explorer 🌈</h1>

          <button
            onClick={restartGame}
            className="flex items-center space-x-2 bg-white rounded-2xl px-4 py-2 shadow-lg border-2 border-safari-200 hover:shadow-xl transition-all duration-200"
          >
            <SafeIcon icon={FiRefreshCw} className="text-jungle-600" />
            <span className="font-comic text-jungle-700">Restart</span>
          </button>
        </motion.div>

        {/* Progress */}
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${(questionsCompleted / 5) * 100}%` }}
          className="h-3 bg-pink-400 rounded-full mb-8 shadow-inner"
        >
          <div className="w-full h-full bg-gradient-to-r from-pink-400 to-purple-500 rounded-full"></div>
        </motion.div>

        {/* Question */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-8 shadow-xl border-4 border-safari-200 mb-8"
        >
          <h2 className="text-2xl font-kid text-center text-jungle-700 mb-6">
            What color is this?
          </h2>

          {/* Color Display */}
          <div className="flex justify-center mb-8">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              className="relative"
            >
              <div
                className="w-32 h-32 rounded-full border-4 border-white shadow-2xl flex items-center justify-center text-4xl"
                style={{ backgroundColor: currentQuestion.targetColor.color }}
              >
                {currentQuestion.targetColor.emoji}
              </div>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                className="absolute -inset-2 rounded-full border-4 border-dashed border-safari-400 opacity-50"
              ></motion.div>
            </motion.div>
          </div>

          {/* Answer Options */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {currentQuestion.answers.map((answer) => (
              <motion.button
                key={answer.name}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => !showResult && handleAnswerSelect(answer.name)}
                disabled={showResult}
                className={`p-6 rounded-2xl font-kid text-lg shadow-lg transition-all duration-200 ${
                  showResult
                    ? answer.name === currentQuestion.correctAnswer
                      ? 'bg-green-400 text-white'
                      : answer.name === selectedAnswer
                      ? 'bg-red-400 text-white'
                      : 'bg-gray-200 text-gray-500'
                    : 'bg-gradient-to-br from-safari-200 to-safari-300 text-jungle-700 hover:from-safari-300 hover:to-safari-400'
                }`}
              >
                <div className="flex flex-col items-center space-y-2">
                  <div
                    className="w-8 h-8 rounded-full border-2 border-white shadow-md"
                    style={{ backgroundColor: answer.color }}
                  ></div>
                  <span className="capitalize">{answer.name}</span>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Result Message */}
          <AnimatePresence>
            {showResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-6 text-center"
              >
                {selectedAnswer === currentQuestion.correctAnswer ? (
                  <div className="text-green-600 font-kid text-xl">
                    🎉 Perfect! That's {currentQuestion.targetColor.name}!
                  </div>
                ) : (
                  <div className="text-red-600 font-kid text-xl">
                    Good try! This color is {currentQuestion.targetColor.name}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default ColorGame;